import { PartialDateRange } from "./misc";

export enum RequestAnalysisStatus {
    Unavailable,
    Available,
    InProgress,
    Completed,
    Failed,
    RetryExceeded,
}

export interface RequestAnalysisData {
    readonly requestType?: string;
    readonly requestPurpose?: string;
    readonly requestTypeMapped?: number;
    readonly requestPurposeMapped?: number;
    readonly patientFamilyName?: string;
    readonly patientGivenName?: string;
    readonly patientDateOfBirth?: Date;
    readonly caseNumber?: string;
    readonly clinicName?: string;
    readonly authorizationDate?: Date;
    readonly requestDate?: Date;
    readonly receivedDate?: Date;
    readonly dueDate?: Date;
    readonly serviceDates: readonly PartialDateRange[];
    readonly requestorName?: string;
    readonly requestorAddress1?: string;
    readonly requestorAddress2?: string;
    readonly requestorCity?: string;
    readonly requestorState?: string;
    readonly requestorStateId?: number;
    readonly requestorZipCode?: string;
    readonly requestorContactFamilyName?: string;
    readonly requestorContactGivenName?: string;
    readonly requestorContactFax?: string;
    readonly requestorContactEmail?: string;
    readonly requestorSiteCode?: string;
    readonly infoReleaseSections: readonly string[];
    readonly sensitiveInfoDeclarations: readonly string[];
}

// Confidence + grounding contract (Spec #34100 / F1b-rois). ROIS derives
// nothing it renders: GroundingState is passed through verbatim from the
// F1b-api extraction contract (#34112) and the React UI maps it to a chip
// color. Per TDD #31604 (lines 107/187) non-match extraction fields are
// green-if-grounded / red-if-not and never yellow (yellow is reserved for
// F1c #34101 patient/requestor multiple-match), so Grounded -> green,
// NotGrounded / InferredNoSource -> red, absent/unmapped -> neutral.
export enum GroundingState {
    Grounded = 0,
    NotGrounded = 1,
    InferredNoSource = 2,
}

// ROIS-side, coordinate-only grounding descriptor. The contract's
// FieldGrounding.CoveringLine is source-document text (PHI) and is NEVER
// copied here, returned in the DTO, or logged (AC12, hipaa-compliance.md) -
// this record intentionally has no text member.
export interface FieldGrounding {
    readonly field: string;
    readonly pageNumber: number;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
}

export interface RequestAnalysis {
    readonly facilityId: number,
    readonly requestBufferId: number,
    readonly associatedDocumentId?: number,
    readonly analysisId?: string,
    readonly createdAtUtc?: Date,
    readonly lastAttemptedAtUtc?: Date,
    readonly completedAtUtc?: Date,
    readonly status: RequestAnalysisStatus,
    readonly data?: RequestAnalysisData,
    readonly dataApprovals?: RequestAnalysisDataApprovals,
    // Per-field confidence, keyed by field name; absent on the persisted
    // re-read path (AC11 - confidence/grounding are read-time/transient only).
    readonly confidence?: Readonly<Record<string, GroundingState>>,
    // Coordinate-only groundings; empty/absent when the Intake-API-side
    // intake.grounding.locatedGate is OFF (AC7/AC9 - tolerate both states).
    readonly fieldGroundings?: readonly FieldGrounding[]
}

export type RequestAnalysisDataApprovals = {
    readonly [key in keyof RequestAnalysisData]?: boolean | null;
};

function tryParseDate(date?: string | null): Date | undefined {
    if (!date || date.startsWith("1900-01-01") || date.startsWith("0001-01-01")) {
        return undefined;
    }
    const output = new Date(date);
    return output.getTime() ? output : undefined;
}

// Coordinate-only projection of the contract grounding (Spec #34100 / F1b-rois).
// Copies ONLY the geometry fields - any CoveringLine (PHI) on the incoming
// object is dropped by construction, never surfaced to the UI (AC12).
function toFieldGrounding(g: Partial<FieldGrounding> & Record<string, unknown>): FieldGrounding {
    return {
        field: g.field ?? "",
        pageNumber: g.pageNumber ?? 0,
        x: g.x ?? 0,
        y: g.y ?? 0,
        width: g.width ?? 0,
        height: g.height ?? 0,
    };
}

export function IntakeFromApiResponse(responseBody?: any): RequestAnalysis | undefined {
    return responseBody && {
        ...responseBody,
        createdAtUtc: tryParseDate(responseBody.createdAtUtc),
        lastAttemptedAtUtc: tryParseDate(responseBody.lastAttemptedAtUtc),
        completedAtUtc: tryParseDate(responseBody.completedAtUtc),
        // Confidence + grounding pass-through, tolerant of absence (contract
        // gap / locatedGate OFF -> undefined, never a throw). Groundings are
        // re-projected coords-only so no CoveringLine can leak through (AC12).
        confidence: responseBody.confidence ?? undefined,
        fieldGroundings: Array.isArray(responseBody.fieldGroundings)
            ? responseBody.fieldGroundings.map(toFieldGrounding)
            : undefined,
        data: responseBody.data && {
            ...responseBody.data,
            patientDateOfBirth: tryParseDate(responseBody.data.patientDateOfBirth),
            authorizationDate: tryParseDate(responseBody.data.authorizationDate),
            requestDate: tryParseDate(responseBody.data.requestDate),
            receivedDate: tryParseDate(responseBody.data.receivedDate),
            dueDate: tryParseDate(responseBody.data.dueDate),
            serviceDates: (responseBody.data.serviceDates as any[]).map(d => ({
                from: tryParseDate(d.from),
                to: tryParseDate(d.to),
            })).filter(d => d.from || d.to) as PartialDateRange[],
            sensitiveInfoDeclarations: (responseBody.data.sensitiveInfoDeclarations as string[]).filter(x => x),
            infoReleaseSections: (responseBody.data.infoReleaseSections as string[]).filter(x => x),
        }
    }
}

export default RequestAnalysis;

// Row anatomy for the shared Quick Entry field-group list renderer (Spec #34098 / F1a).
// Sibling specs (F1b-rois #34100, F1c #34101, F3 #34099) implement their own row
// component against this contract; F1a owns and is the only Spec editing this
// shared portion of models/intake.ts.
export enum FieldGroupRowStatus {
    NeedsEntry = 0,       // empty + required -> sort tier 0
    LowConfidence = 1,    // AI-populated but below confidence threshold -> tier 1
    EmptyNotRequired = 2, // empty + not required -> tier 2
    Confirmed = 3,        // AI-confirmed or user-entered -> tier 3
}

export interface FieldGroupRow {
    readonly id: string;                    // stable key, e.g. form field name
    readonly badgeNumber: number;           // 1-based, renumbered on show/hide/reorder
    readonly status: FieldGroupRowStatus;
    readonly label: string;
    readonly value: string | null;
    readonly isAiPreSelected: boolean;      // drives the "AI pre-selected" chip; F1a's
                                             // one wired row (Request Type/Purpose) uses
                                             // this only - no confidence-dot styling here,
                                             // that's F1b-rois's (#34100) domain.
}

// Sort-tier lookup used by the "Needs attention first" toggle.
export const FIELD_GROUP_ROW_SORT_TIER: Readonly<Record<FieldGroupRowStatus, number>> = {
    [FieldGroupRowStatus.NeedsEntry]: 0,
    [FieldGroupRowStatus.LowConfidence]: 1,
    [FieldGroupRowStatus.EmptyNotRequired]: 2,
    [FieldGroupRowStatus.Confirmed]: 3,
};

/**
 * A row counts as "outstanding" (drives N in AC9/AC10) unless it has been
 * verified. Shared between QuickEntryFieldGroupList (renders the rows) and
 * DataEntryDetails.tsx (owns N for the morphing action button, which lives
 * in DataEntryDetailsLeftPane's footer per the mockup's .action-footer -
 * both need the identical definition of "outstanding", not a duplicate.
 */
export function isFieldGroupRowOutstanding(row: FieldGroupRow, verifiedLowConfidenceIds: ReadonlySet<string>): boolean {
    if (row.status === FieldGroupRowStatus.NeedsEntry) {
        return true;
    }
    if (row.status === FieldGroupRowStatus.LowConfidence) {
        return !verifiedLowConfidenceIds.has(row.id);
    }
    return false;
}

/** attn = tier-0 (NeedsEntry) + tier-1 (LowConfidence) rows, regardless of verification (Business Rule 10). */
export function isFieldGroupRowAttention(row: FieldGroupRow): boolean {
    return row.status === FieldGroupRowStatus.NeedsEntry || row.status === FieldGroupRowStatus.LowConfidence;
}
